import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createAimsDocumentTypeAsync,
  getAimsDocumentTypeAsync,
  updateAimsDocumentTypeAsync
} from './aimsDocumentTypeSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    }
  })
);

interface CreateEditAimsDocumentType {
  id: number | null;
  name: string | null;
}

export default function AimsDocumentTypeCreateEdit() {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { selectedAimsDocumentType } = useSelector(
    (state: RootState) => state.aimsDocumentTypes
  );

  const params = useParams<{ aimsDocumentTypeId: string }>();
  const aimsDocumentTypeId = Number.parseInt(params.aimsDocumentTypeId, 10);
  const isNewPage = !Boolean(aimsDocumentTypeId);

  const [aimsDocumentType, setAimsDocumentType] =
    useState<CreateEditAimsDocumentType>({
      id: null,
      name: null
    });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedAimsDocumentType) {
      setAimsDocumentType({
        ...aimsDocumentType,
        id: selectedAimsDocumentType.id,
        name: selectedAimsDocumentType.name
      });
    }
  }, [selectedAimsDocumentType]);

  useEffect(() => {
    if (!isNewPage) dispatch(getAimsDocumentTypeAsync(aimsDocumentTypeId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setAimsDocumentType({
      ...aimsDocumentType,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (aimsDocumentType.name) {
        if (isNewPage) {
          // create aims document type
          const createAimsDocumentType = {
            name: aimsDocumentType.name
          };
          await dispatch(
            createAimsDocumentTypeAsync(createAimsDocumentType)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('aimsDocumentTypeCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update aims document type
          const updateAimsDocumentType = {
            id: aimsDocumentType.id,
            name: aimsDocumentType.name
          };
          await dispatch(
            updateAimsDocumentTypeAsync(updateAimsDocumentType)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('aimsDocumentTypeCreateEdit.messages.updateSuccess')
            })
          );
        }
      } else {
        throw new Error(t('aimsDocumentTypeCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('aimsDocumentTypeCreateEdit.breadcrumb.masterDataName') as string,
          masterDataRef: 'aims-document-types',
          masterDataSubName: selectedAimsDocumentType?.name,
          masterDataSubRef: selectedAimsDocumentType?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('aimsDocumentTypeCreateEdit.title.new') : t('aimsDocumentTypeCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('aimsDocumentTypeCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            id="aims-document-type-name"
            label={t('aimsDocumentTypeCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={aimsDocumentType.name ? aimsDocumentType.name : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('aimsDocumentTypeCreateEdit.buttons.add') : t('aimsDocumentTypeCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
